<template>
  <div class="answer">
    <base-select
      v-if="type === Types.Dropdown"
      :options="items"
      class="mt-10 cursor-pointer"
      v-model:selectedItem="selectedItems"
    ></base-select>
    <template v-else>
      <div
        v-for="(item, index) in items"
        :key="item.uuid"
        class="answer-item mt-2 grid relative md:hover:text-white transition duration-200"
        :class="{ checked: selectedItems.includes(item.uuid) }"
        @mouseover="changeImage($event, item)"
        @mouseleave="changeImage($event, null)"
        @touchstart="checkTouch"
        :data-test="`answers ${Types.Multiple === type ? 'multiple' : 'single'}`"
      >
        <base-checkbox
          v-if="type === Types.Multiple"
          v-model:selectedItems="selectedItems"
          :uuid="item.uuid"
          :value="item.uuid"
          class="py-1 col-frist"
          :data-test="`answer-checkbox`"
          >{{ item.text }}</base-checkbox
        >
        <base-checkbox
          v-if="type === Types.Single"
          v-model:selectedItem="selectedItems"
          :uuid="item.uuid"
          :value="item.uuid"
          :checked="item.uuid === selectedItems[0]"
          class="py-1 col-frist"
          :data-test="`answer-checkbox`"
          >{{ item.text }}</base-checkbox
        >
        <div class="col-last flex">
          <module-tooltip v-show="item.hint" :hint="item.hint" class="md:self-center py-2 px-6" />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import ModuleTooltip from '@/components/module/ModuleTooltip.vue'
import { Answer, Types } from '@/types/answer'
import { useMainStore } from '@/store/main'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: {
    BaseCheckbox,
    BaseSelect,
    ModuleTooltip,
  },
  props: {
    questionId: {
      type: String,
      required: true,
    },
    items: {
      type: Array as PropType<Array<Answer>>,
      default: () => [],
    },
    type: {
      type: String as PropType<Types>,
      default: Types.Single,
    },
  },
  setup(props, { emit }) {
    const selectedItems = ref<string[]>([])

    /**
     * register Store
     */
    const store = useMainStore()

    /**
     * register Route
     */
    const route = useRoute()

    /**
     * @emits update:answers
     * @event answers
     */
    watch(selectedItems, (newValue) => {
      store.setAnswer(newValue)

      selectImage()
    })

    /**
     * @returns {string|number} id
     */
    const id = computed(() => route.params.id)

    /**
     * @description watch router param and set store page
     * get saved answered from store
     */
    watch(id, (newValue) => {
      if (id.value) {
        const answered = store.getAnswers
        if (answered) {
          selectedItems.value = answered
        }
        // Reset just for drop, because dropdown fills automatically
        else if (props.type !== Types.Dropdown) {
          selectedItems.value = []
        }
        selectImage()
      }
    })

    const isTouch = ref(false)

    /**
     * @emits changeImage
     * @props {MouseEvent} $event
     * @props {Image | Boolean} image
     */
    const changeImage = (event: MouseEvent, answer: Answer | null) => {
      if (!isTouch.value) emit('changeImage', answer)
    }
    /**
     * @emits selectImage
     * @props {Image | Boolean} image
     */
    const selectImage = () => {
      let selectedAnswer = null
      selectedItems.value.forEach((selectedItem) => {
        selectedAnswer = props.items.find((item) => item.uuid === selectedItem) ?? null
      })
      emit('selectImage', selectedAnswer)
    }
    const checkTouch = () => {
      isTouch.value = true
    }

    return { selectedItems, Types, changeImage, checkTouch }
  },
})
</script>

<style scoped>
.answer-item {
  grid-template-columns: minmax(min-content, 1fr) minmax(min-content, max-content);
}
.col-first {
  width: 100%;
}
.checked {
  @apply text-white bg-black;
}
.answer-item {
  @apply relative;
}
.answer-item::before {
  content: '';
  @apply absolute bg-black inset-0 opacity-0 transition duration-200;
  left: -0.5rem;
  right: -0.5rem;
  z-index: -1;
}

.answer-item.checked::before {
  @apply opacity-100 transition duration-200;
}
.answer-item:hover::before {
  @apply md:opacity-100 transition duration-200;
}
</style>
